import React, { ChangeEvent, ReactNode, ComponentType } from 'react';
import { NumericFormat, PatternFormat, NumberFormatValues } from 'react-number-format';
import OutlinedTextField from './OutlinedTextField';

interface MaskedTextFieldProps {
  id: string;
  name?: string;
  format?: string;
  placeholder?: string;
  value: string | number;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  label?: string;
  patternType?: 'pattern' | 'numeric';
  setValue?: (value: string) => void;
  isClearable?: boolean;
  customInput?: ComponentType<Record<string, unknown>>;
  maxLength?: number;
  onClick?: (event: React.MouseEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  mask?: string;
  error?: boolean;
  helperText?: ReactNode;
  [key: string]: unknown;
}

export default function MaskedTextField({
  id,
  name,
  format = '',
  placeholder,
  value,
  onChange,
  label,
  patternType = 'pattern',
  setValue,
  isClearable,
  customInput = OutlinedTextField,
  maxLength,
  onClick,
  onFocus,
  mask,
  error,
  helperText,
  ...other
}: MaskedTextFieldProps) {
  const Component = patternType === 'numeric' ? NumericFormat : PatternFormat;

  const isAllowed = (values: NumberFormatValues) => {
    if (!maxLength) return true;

    const { value } = values;
    return value.length <= maxLength;
  };

  const customInputProps = {
    error,
    helperText,
    onClick,
    onFocus,
  };

  return (
    <Component
      id={id}
      name={name}
      customInput={customInput}
      placeholder={placeholder}
      format={format}
      value={value}
      mask={mask}
      onValueChange={(values) => {
        onChange({
          target: {
            value: values.value,
            name,
          } as EventTarget & HTMLInputElement,
        } as ChangeEvent<HTMLInputElement>);
      }}
      isClearable={isClearable}
      setValue={
        setValue ||
        ((value: string) => onChange({ target: { value, name } } as ChangeEvent<HTMLInputElement>))
      }
      label={label}
      isAllowed={patternType === 'numeric' ? isAllowed : undefined}
      {...(patternType === 'pattern' && maxLength ? { maxLength } : {})}
      {...customInputProps}
      {...other}
    />
  );
}
